// Where a rule comes from (REQ-AGT-28). The agent serves each criterion's citations resolved
// against the source library; this module turns them into the one short line the UI shows and
// into a criterion-id index, so a finding can name the standard its rule rests on.
//
// The source's name is never part of a criterion's Finnish text. It lives here, read from the
// citation, so a new edition changes it in one place.
import type {
  KBClaimCitation,
  KBCitedSource,
  KBEquipmentType,
  KBOperationalCriterion,
} from "@/lib/api/agent";

/** A claim that resolved to no source names no standard — the UI then says nothing. */
export function isCitationShowable(citation: KBClaimCitation): boolean {
  return citation.sources.length > 0;
}

/**
 * One source as the UI names it: the edition, then where it was read.
 *
 * The edition (`K1/2021`) identifies the document more usefully than its full title, which is a
 * sentence long in Finnish. The title stays available for a tooltip.
 */
export function citedSourceLabel(source: KBCitedSource): string {
  const edition = source.edition || source.ref;
  return source.locator ? `${edition} · ${source.locator}` : edition;
}

/** Every source behind one criterion, in citation order, without repeats. */
export function criterionSourceLabels(
  citations: KBClaimCitation[] | undefined,
): string[] {
  const labels: string[] = [];
  for (const citation of citations ?? []) {
    if (!isCitationShowable(citation)) continue;
    for (const source of citation.sources) {
      const label = citedSourceLabel(source);
      if (!labels.includes(label)) labels.push(label);
    }
  }
  return labels;
}

function collect(
  index: Map<string, KBClaimCitation[]>,
  criteria: KBOperationalCriterion[] | undefined,
): void {
  for (const criterion of criteria ?? []) {
    const citations = (criterion.claims ?? []).filter(isCitationShowable);
    if (citations.length === 0 || index.has(criterion.id)) continue;
    index.set(criterion.id, citations);
  }
}

/**
 * Citations by criterion id, across every place a type's criteria live.
 *
 * A finding's `finding_type` is the criterion id that raised it, so this index is the join
 * between a finding and the standard behind its rule.
 */
export function ruleSourcesByCriterionId(
  kb: KBEquipmentType | null | undefined,
): Map<string, KBClaimCitation[]> {
  const index = new Map<string, KBClaimCitation[]>();
  if (!kb) return index;
  for (const component of kb.components ?? []) {
    collect(index, component.operational_criteria);
  }
  for (const behavior of kb.behaviors ?? []) {
    collect(index, behavior.operational_criteria);
  }
  for (const analysis of kb.analyses ?? []) {
    collect(index, analysis.operational_criteria);
  }
  for (const analysis of kb.connection_analyses ?? []) {
    collect(index, analysis.operational_criteria);
  }
  return index;
}
